import { ChangeEvent } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';

type FormState = {
  started: boolean;
  submitted: boolean;
  fieldInteractionCounts: Record<string, number>;
};

const formStates: Record<string, FormState> = {};

/**
 * Generates a unique form identifier based on the page path and form ID.
 * Used as a key to store and retrieve form state.
 *
 * @param {string} formId - The id of the form element
 * @returns {string} Unique form identifier
 */
function getFormExtraId(formId: string) {
  const path = window.location.pathname.replace(/^\//, '').replace(/\//g, '-');

  if (path === '') {
    return `${formId}`;
  }

  return `${path}-${formId}`;
}

/**
 * Sends a 'form_submit' event to GTM when a form is submitted.
 *
 * @param {string} formId - The id of the form
 * @param {Record<string, any>} data - The form data
 */
export function sendFormSubmitEvent(
  formId: string,
  data: Record<string, any>,
): void {
  const eventData = {
    event: 'form_submit',
    formId,
    data,
  };

  console.log(eventData, 'form_submit');
  sendGTMEvent(eventData);
}

/**
 * Handles a field interaction inside a form.
 * Sends a 'form_start' event once per form and a 'form_field_interaction' event for each field.
 *
 * @param {ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} event - The change/blur event
 */
export function sendFormFieldInteractionEvent(
  event: ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >,
): void {
  const fieldElement = event.target;
  const formElement = fieldElement.form;
  const fieldName = fieldElement.name;
  const fieldValue = fieldElement.value;
  const formExtraId = getFormExtraId(formElement?.id || '');
  const state: FormState = (formStates[formExtraId] ||= {
    started: false,
    submitted: false,
    fieldInteractionCounts: {},
  });

  // Send 'form_start' once
  if (!state.started) {
    state.started = true;
    const startEvent = {
      formId: formExtraId,
      event: 'form_start',
    };
    console.log(startEvent, 'form_start');
    sendGTMEvent(startEvent);
  }

  // Track field interactions
  state.fieldInteractionCounts[fieldName] =
    (state.fieldInteractionCounts[fieldName] || 0) + 1;

  const interactionEvent = {
    event: 'form_field_interaction',
    formId: formExtraId,
    field: fieldName,
    value: fieldValue,
    count: state.fieldInteractionCounts[fieldName],
    timestamp: new Date().toISOString(),
  };

  console.log(interactionEvent, 'form_field_interaction');
  sendGTMEvent(interactionEvent);
}
